import { ServiceOrderPreview, ServiceOrderStatus } from '../serviceOrder.types';
import { FormattedMessage } from 'react-intl';
import { HighlightedFormContainer } from '../../shared/sidebar/HighlightedFormContainer';
import { DayAndTimePicker } from '../../../sharedComponents/DayAndTimePicker';
import { OptionalLabel } from '../../../sharedComponents/OptionalLabel';
import { SubmitEventHandler, useState } from 'react';
import {
    ExecutionDateErrorType,
    getDateValidation,
    ServiceOrderDateField,
    ServiceOrderFormFieldError,
    validateCompletedAt,
    validateStartedAt,
} from '../shared/ServiceOrderFormValidation';
import { getUniqueValueOrUndefined } from '../../../sharedComponents/dataHelper';

export const SERVICE_BULK_UPDATE_FORM_ID = 'SERVICE_BULK_UPDATE_FORM_ID';
export const DEFAULT_TIMEZONE = 'UTC';

export type ServiceOrderBulkUpdateFormState = {
    readonly startedAt: Date | undefined;
    readonly completedAt: Date | undefined;
    readonly timezone: string;
    readonly generalInformation: string | undefined;
};

export const getInitialServiceOrderBulkUpdateFormState = (
    validOrders: ServiceOrderPreview[],
): ServiceOrderBulkUpdateFormState => ({
    startedAt: getUniqueValueOrUndefined(
        validOrders.map((order) => order.startedAt).filter((startedAt) => startedAt !== undefined),
    ),
    completedAt: getUniqueValueOrUndefined(
        validOrders.map((order) => order.completedAt).filter((completedAt) => completedAt !== undefined),
    ),
    timezone: getUniqueValueOrUndefined(validOrders.map((order) => order.timeZone)) ?? DEFAULT_TIMEZONE,
    generalInformation: getUniqueValueOrUndefined(
        validOrders
            .map((order) => order.generalInformation)
            .filter((generalInformation) => generalInformation !== undefined),
    ),
});

type ServiceOrderBulkUpdateFormStepProps = {
    validOrders: ServiceOrderPreview[];
    setDateParseError: (field: ServiceOrderDateField, hasParseError: boolean) => void;
    isDisabled: boolean;
    handleOnSave: (formState: ServiceOrderBulkUpdateFormState) => void;
};

export type ServiceOrderBulkUpdateFormErrors = {
    startedAt?: ServiceOrderFormFieldError<ExecutionDateErrorType>;
    completedAt?: ServiceOrderFormFieldError<ExecutionDateErrorType>;
};

// eslint-disable-next-line max-lines-per-function
export const ServiceOrderBulkUpdateFormStep = ({
    validOrders,
    setDateParseError,
    isDisabled,
    handleOnSave,
}: ServiceOrderBulkUpdateFormStepProps) => {
    const [formState, setFormState] = useState<ServiceOrderBulkUpdateFormState>(() =>
        getInitialServiceOrderBulkUpdateFormState(validOrders),
    );
    const setStartDateFormState = (date: Date | undefined) => setFormState({ ...formState, startedAt: date });
    const setCompletedDateFormState = (date: Date | undefined) => setFormState({ ...formState, completedAt: date });

    const [formErrors, setFormErrors] = useState<ServiceOrderBulkUpdateFormErrors>({});
    const validateForm = (): boolean => {
        const newFormErrors: ServiceOrderBulkUpdateFormErrors = {};

        const alreadyStartedOrder = validOrders.find((order) => order.startedAt !== undefined);
        newFormErrors.startedAt = validateStartedAt(formState.startedAt, alreadyStartedOrder?.startedAt);

        // TODO PORTALOUT-3812 use the status provided by the backend similiar to the sidebar logic
        const completedOrder = validOrders.find((order) => order.completedAt !== undefined);
        newFormErrors.completedAt = validateCompletedAt(
            formState.startedAt,
            formState.completedAt,
            // TODO PORTALOUT-3812 not correct to set to open
            completedOrder ? ServiceOrderStatus.COMPLETED : ServiceOrderStatus.OPEN,
        );
        setFormErrors(newFormErrors);
        return Object.values(newFormErrors).some((error) => error !== undefined);
    };

    const onSubmit: SubmitEventHandler<HTMLFormElement> = (event) => {
        event.preventDefault();
        const hasError = validateForm();
        if (!hasError) {
            handleOnSave(formState);
        }
    };

    return (
        <form id={SERVICE_BULK_UPDATE_FORM_ID} onSubmit={onSubmit}>
            <HighlightedFormContainer instructionMessageId="outboundPortal.serviceOrder.sidebar.pleaseProvide">
                <div className="display-flex align-items-start flex-row flex-wrap gap-10 margin-top-20 margin-bottom-20">
                    <div className="flex-1-1 max-width-50pct">
                        <DayAndTimePicker
                            labelId="outboundPortal.serviceOrder.sidebar.executionStartDate"
                            timeZone={formState.timezone}
                            date={formState.startedAt}
                            validation={getDateValidation(formErrors.startedAt)}
                            onDateChange={setStartDateFormState}
                            isDisabled={isDisabled}
                            onParseError={(hasParseError) => setDateParseError('startedAt', hasParseError)}
                        />
                    </div>

                    <div className="flex-1-1 max-width-50pct">
                        <DayAndTimePicker
                            labelId="outboundPortal.serviceOrder.sidebar.executionEndDate"
                            timeZone={formState.timezone}
                            date={formState.completedAt}
                            validation={getDateValidation(formErrors.completedAt)}
                            onDateChange={setCompletedDateFormState}
                            isDisabled={isDisabled}
                            onParseError={(hasParseError) => setDateParseError('completedAt', hasParseError)}
                        />
                    </div>
                </div>
                <div>
                    <OptionalLabel isOptional htmlFor="generalInformation">
                        <FormattedMessage id="outboundPortal.serviceOrder.sidebar.generalInformation" />
                    </OptionalLabel>
                    <textarea
                        id="generalInformation"
                        disabled={isDisabled}
                        className="width-100pct form-control"
                        style={{ resize: 'none' }}
                        rows={5}
                        maxLength={255}
                        value={formState.generalInformation}
                        onChange={(text) => {
                            setFormState({
                                ...formState,
                                generalInformation: text.target.value,
                            });
                        }}
                    />
                </div>
            </HighlightedFormContainer>
        </form>
    );
};
